import React, { useState, useMemo, useRef, useEffect } from 'react';

/**
 * CameraPicker — the conf-1 "which camera" picker (#86bb7phhh S32, David). At conf-1 the shared
 * CamButtonsView degrades into a per-camera horizontal scroller (one button per cam, number-only, and on
 * the narrow wide-tier strip its ‹ › arrows don't even render) — undiscoverable + unusable to reach a
 * distant camera on a big system. This replaces that with a proper searchable LIST: every selectable
 * single-cam set as a row showing the camera NAME (via labelFor), the current one highlighted + scrolled
 * into view, filter-as-you-type. Rendered inside the shared <Sheet> (`.mcpb-sheet--campick`) so it's a
 * bottom sheet on tablet and a centered modal on desktop, matching every other playback selector.
 *
 * The inline ‹ [current name] › stepper stays in the camera bar for one-at-a-time next/prev (David's
 * refinement); this picker is the "jump to any camera" path.
 *
 * Props:
 *   sets      — ordered set strings for the current conf (conf-1 → one per camera, e.g. ['cam_1','cam_2'])
 *   current   — the selected set string (currentCamView)
 *   rowFor    — (set) => { num, label }; the camera NUMBER + its descriptive label (name / "NVR : Name")
 *   onSelect  — (set) => void; pick a camera (closes the sheet in the parent)
 */
const CameraPicker = ({ sets = [], current, rowFor, onSelect }) => {
  const [q, setQ] = useState('');
  const inputRef = useRef(null);
  const activeRowRef = useRef(null);

  // Resolve every set to its number + label once (rowFor hits the camera array per set).
  const rows = useMemo(
    () => (sets || []).map((set) => {
      const parts = (rowFor ? rowFor(set) : null) || {};
      return { set, num: parts.num != null ? String(parts.num) : '', label: parts.label || String(set) };
    }),
    [sets, rowFor],
  );
  const needle = q.trim().toLowerCase();
  // Match on BOTH the number and the name so typing "5" or "front" both find camera 5 Front Door.
  const filtered = needle ? rows.filter((r) => `${r.num} ${r.label}`.toLowerCase().includes(needle)) : rows;

  // Focus the search on open + scroll the current camera into view so a big list opens ON the selection.
  useEffect(() => {
    if (activeRowRef.current && activeRowRef.current.scrollIntoView) {
      activeRowRef.current.scrollIntoView({ block: 'center' });
    }
    if (inputRef.current) inputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mcpb-campick">
      <input
        ref={inputRef}
        className="mcpb-campick__search"
        type="search"
        inputMode="search"
        placeholder="Search cameras…"
        aria-label="Search cameras"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="mcpb-campick__list" role="listbox" aria-label="Cameras">
        {filtered.length === 0 && (
          <div className="mcpb-campick__empty">No cameras match &ldquo;{q}&rdquo;</div>
        )}
        {filtered.map((r) => {
          const active = r.set === current;
          return (
            <button
              key={r.set}
              type="button"
              role="option"
              aria-selected={active}
              ref={active ? activeRowRef : null}
              className={`mcpb-campick__row${active ? ' mcpb-campick__row--active' : ''}`}
              onClick={() => onSelect && onSelect(r.set)}
            >
              {r.num !== '' && <span className="mcpb-campick__row-num">{r.num}</span>}
              <span className="mcpb-campick__row-label">{r.label}</span>
              {active && <span className="mcpb-campick__row-check" aria-hidden="true">&#10003;</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CameraPicker;
